import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ref, onValue } from 'firebase/database';
import Navbar from './Navbar';
import Footer from './Footer';
import { db } from '../firebase';
import { 
  FaArrowLeft, 
  FaBoxOpen, 
  FaCalendarAlt, 
  FaReceipt,
  FaShippingFast,
  FaCheckCircle,
  FaClock,
  FaTimesCircle,
  FaChevronRight,
  FaSearch,
  FaFilter,
  FaSort,
  FaShoppingBag,
  FaStar,
  FaEye,
  FaMapMarkerAlt,
  FaGift
} from 'react-icons/fa';
import { HiOutlineSparkles } from 'react-icons/hi';
import styled, { keyframes } from 'styled-components';

// Enhanced Keyframe Animations
const slideInFromBottom = keyframes`
  from {
    transform: translateY(50px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

const slideInFromLeft = keyframes`
  from {
    transform: translateX(-30px);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
`;

const fadeInUp = keyframes`
  from {
    transform: translateY(20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
`;

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const floatAnimation = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-8px); }
`;

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const sparkle = keyframes`
  0%, 100% { opacity: 0; transform: scale(0.8) rotate(0deg); }
  50% { opacity: 1; transform: scale(1) rotate(180deg); }
`;

// Enhanced Styled Components
const PageContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  position: relative;
  overflow: hidden;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background:
      radial-gradient(circle at 20% 50%, rgba(99, 102, 241, 0.05) 0%, transparent 50%),
      radial-gradient(circle at 80% 20%, rgba(236, 72, 153, 0.05) 0%, transparent 50%),
      radial-gradient(circle at 40% 80%, rgba(14, 165, 233, 0.05) 0%, transparent 50%);
    animation: ${floatAnimation} 20s ease-in-out infinite;
  }
`;

const MainContent = styled.div`
  position: relative;
  z-index: 1;
  padding: 2rem;
  max-width: 1400px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 2rem;
  color: #1f2937;
  cursor: pointer;
  background: rgba(255, 255, 255, 0.8);
  border: 2px solid rgba(99, 102, 241, 0.2);
  font-size: 1rem;
  font-weight: 600;
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  padding: 0.75rem 1.5rem;
  border-radius: 50px;
  backdrop-filter: blur(10px);
  animation: ${fadeInUp} 0.6s ease-out;

  &:hover {
    background: rgba(99, 102, 241, 0.1);
    transform: translateX(-8px) translateY(-2px);
    box-shadow: 0 10px 30px rgba(99, 102, 241, 0.2);
  }

  svg {
    transition: transform 0.3s ease;
  }

  &:hover svg {
    transform: translateX(-5px);
  }
`;

const PageHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 3rem;
  animation: ${fadeInUp} 0.8s ease-out 0.2s both;
  
  @media (min-width: 768px) {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }
`;

const PageTitle = styled.h1`
  font-size: 3rem;
  font-weight: 800;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin: 0;
  line-height: 1.2;
  position: relative;

  &:after {
    content: '📦';
    position: absolute;
    top: -10px;
    right: -40px;
    font-size: 2rem;
    animation: ${sparkle} 2s ease-in-out infinite;
  }

  @media (max-width: 768px) {
    font-size: 2.2rem;

    &:after {
      right: -30px;
      font-size: 1.5rem;
    }
  }
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    width: 100%;
  }
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem;
  background: rgba(255, 255, 255, 0.8);
  color: #1f2937;
  border: 2px solid rgba(99, 102, 241, 0.2);
  border-radius: 50px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);

  &:hover {
    background: rgba(99, 102, 241, 0.1);
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(99, 102, 241, 0.2);
  }

  @media (max-width: 768px) {
    flex: 1;
    justify-content: center;
  }
`;

const OrdersGrid = styled.div`
  display: grid;
  gap: 2rem;
  animation: ${fadeInUp} 0.6s ease-out 0.4s both;
  
  @media (min-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  }
`;

const OrderCard = styled.div`
  background: rgba(255, 255, 255, 0.95);
  border-radius: 24px;
  padding: 2rem;
  box-shadow: 
    0 20px 40px rgba(0, 0, 0, 0.1),
    0 0 0 1px rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(20px);
  transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
  position: relative;
  overflow: hidden;
  animation: ${slideInFromBottom} 0.8s ease-out both;
  animation-delay: ${props => (props.index * 0.1)}s;
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent);
    transition: left 0.8s ease;
  }
  
  &:hover {
    transform: translateY(-8px) scale(1.02);
    box-shadow: 
      0 30px 60px rgba(0, 0, 0, 0.15),
      0 0 0 1px rgba(102, 126, 234, 0.2);
    
    &:before {
      left: 100%;
    }
  }
`;

const OrderHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1.5rem;
  gap: 1rem;
  
  @media (max-width: 480px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const OrderTitle = styled.h3`
  font-size: 1.4rem;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const StatusBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: ${props => props.bgColor};
  color: ${props => props.textColor};
  padding: 0.75rem 1.25rem;
  border-radius: 50px;
  font-size: 0.9rem;
  font-weight: 600;
  white-space: nowrap;
  box-shadow: 0 4px 15px ${props => props.bgColor}40;
  animation: ${pulse} 2s ease-in-out infinite;
  
  svg {
    color: ${props => props.iconColor};
    font-size: 1rem;
  }
`;

const OrderMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: #666;
  font-size: 0.95rem;
  margin-bottom: 1.5rem;
  padding: 0.75rem 1rem;
  background: linear-gradient(135deg, #f8f9ff 0%, #e8f0ff 100%);
  border-radius: 16px;
  border-left: 4px solid #667eea;
  
  svg {
    color: #667eea;
    font-size: 1.1rem;
  }
`;

const ItemsContainer = styled.div`
  margin-bottom: 1.5rem;
  background: linear-gradient(135deg, #f8f9ff 0%, #e8f0ff 100%);
  border-radius: 16px;
  padding: 1.5rem;
  border: 1px solid rgba(102, 126, 234, 0.1);
`;

const ItemRow = styled.div`
  display: flex;
  gap: 1rem;
  padding: 1rem 0;
  border-bottom: 1px solid rgba(102, 126, 234, 0.1);
  transition: all 0.3s ease;
  
  &:last-child {
    border-bottom: none;
    margin-bottom: 0;
  }
  
  &:hover {
    transform: translateX(8px);
    background: rgba(255, 255, 255, 0.5);
    border-radius: 12px;
    margin: 0 -0.5rem;
    padding: 1rem 1.5rem;
  }
`;

const ItemImage = styled.img`
  width: 80px;
  height: 80px;
  object-fit: cover;
  border-radius: 16px;
  border: 2px solid rgba(102, 126, 234, 0.1);
  transition: all 0.3s ease;
  
  &:hover {
    transform: scale(1.1) rotate(2deg);
    box-shadow: 0 8px 20px rgba(102, 126, 234, 0.3);
  }
`;

const ItemDetails = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const ItemName = styled.p`
  font-weight: 600;
  color: #1a1a1a;
  margin: 0;
  font-size: 1.05rem;
  line-height: 1.4;
`;

const ItemPrice = styled.p`
  color: #667eea;
  font-weight: 600;
  margin: 0;
  font-size: 1rem;
`;

const ItemMeta = styled.p`
  color: #666;
  font-size: 0.85rem;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const DiscountTag = styled.span`
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
`;

const MoreItemsIndicator = styled.p`
  color: #667eea;
  font-size: 0.9rem;
  font-weight: 600;
  margin: 1rem 0 0 0;
  text-align: center;
  padding: 0.75rem;
  background: rgba(102, 126, 234, 0.1);
  border-radius: 12px;
  border: 1px dashed #667eea;
`;

const OrderFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 1.5rem;
  border-top: 2px solid rgba(102, 126, 234, 0.1);
  gap: 1rem;
  
  @media (max-width: 480px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const ViewDetailsButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.875rem 1.5rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 50px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  box-shadow: 0 8px 20px rgba(102, 126, 234, 0.3);
  position: relative;
  overflow: hidden;
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: left 0.5s ease;
  }
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 12px 30px rgba(102, 126, 234, 0.4);
    
    &:before {
      left: 100%;
    }
  }
  
  svg {
    transition: transform 0.3s ease;
  }
  
  &:hover svg {
    transform: translateX(4px);
  }
`;

const TotalPrice = styled.div`
  font-weight: 800;
  color: #1a1a1a;
  font-size: 1.5rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  position: relative;
  
  &:before {
    content: '💰';
    position: absolute;
    left: -30px;
    top: 50%;
    transform: translateY(-50%);
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 24px;
  backdrop-filter: blur(20px);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  animation: ${fadeInUp} 0.8s ease-out;
  
  .icon {
    font-size: 4rem;
    color: #d1d5db;
    margin-bottom: 1.5rem;
    animation: ${floatAnimation} 3s ease-in-out infinite;
  }
  
  h3 {
    font-size: 1.5rem;
    color: #1a1a1a;
    margin-bottom: 1rem;
  }
  
  p {
    color: #666;
    margin-bottom: 2rem;
    font-size: 1.1rem;
  }
`;

const CTAButton = styled.button`
  padding: 1rem 2rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 50px;
  font-weight: 600;
  font-size: 1.1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 8px 20px rgba(102, 126, 234, 0.3);
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 12px 30px rgba(102, 126, 234, 0.4);
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 60vh;
  gap: 2rem;
  color: #1f2937;

  .spinner {
    width: 60px;
    height: 60px;
    border: 4px solid rgba(99, 102, 241, 0.3);
    border-top: 4px solid #6366f1;
    border-radius: 50%;
    animation: ${spin} 1s linear infinite;
  }

  p {
    font-size: 1.2rem;
    font-weight: 600;
  }
`;

const OrdersPage = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortBy, setSortBy] = useState('date');
  const [filterStatus, setFilterStatus] = useState('all');
  const user = JSON.parse(localStorage.getItem('userData'));

  useEffect(() => {
    if (!user || !user.id) {
      setLoading(false);
      return;
    }

    const userRef = ref(db, `Watch/customers/${user.id}`);
    const unsubscribeUser = onValue(userRef, (snapshot) => {
      const userData = snapshot.val();
      const userMobile = userData?.phone || userData?.mobile;

      if (!userMobile) {
        setError('Mobile number not found in profile');
        setLoading(false);
        return;
      }

      const ordersRef = ref(db, `Watch/customers/${userMobile}/orders`);
      const unsubscribeOrders = onValue(ordersRef, (snapshot) => {
        const ordersData = snapshot.val();
        if (ordersData) {
          const ordersArray = Object.entries(ordersData).map(([key, value]) => ({
            id: key,
            ...value,
          }));
          ordersArray.sort((a, b) => new Date(b.date || b.createdAt) - new Date(a.date || a.createdAt));
          setOrders(ordersArray);
          setError(null);
        } else {
          setOrders([]);
        }
        setLoading(false);
      }, (error) => {
        console.error('Error fetching orders:', error);
        setError('Failed to load orders');
        setLoading(false);
      });

      return () => unsubscribeOrders();
    }, (error) => {
      console.error('Error fetching user data:', error);
      setError('Failed to load user profile');
      setLoading(false);
    });

    return () => unsubscribeUser();
  }, [user]);

  const getFirstTwoItems = (items) => {
    if (!items) return [];
    if (typeof items === 'object' && !Array.isArray(items)) {
      return Object.values(items).slice(0, 2);
    }
    return items.slice(0, 2);
  };

  const getTotalItemsCount = (items) => {
    if (!items) return 0;
    if (typeof items === 'object' && !Array.isArray(items)) {
      return Object.keys(items).length;
    }
    return items.length;
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'pending': return { bg: '#FEF3C7', text: '#92400E', icon: '#F59E0B' };
      case 'processing': return { bg: '#E0E7FF', text: '#5B21B6', icon: '#8B5CF6' };
      case 'shipped': return { bg: '#DBEAFE', text: '#1E40AF', icon: '#3B82F6' };
      case 'delivered': return { bg: '#D1FAE5', text: '#065F46', icon: '#10B981' };
      case 'cancelled': return { bg: '#FEE2E2', text: '#991B1B', icon: '#EF4444' };
      default: return { bg: '#F3F4F6', text: '#374151', icon: '#9CA3AF' };
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'pending': return <FaClock />;
      case 'processing': return <FaClock />;
      case 'shipped': return <FaShippingFast />;
      case 'delivered': return <FaCheckCircle />;
      case 'cancelled': return <FaTimesCircle />;
      default: return <FaClock />;
    }
  };

  return (
    <>
      <Navbar />
      <PageContainer>
        <MainContent>
          <BackButton onClick={() => navigate(-1)}>
            <FaArrowLeft /> Back
          </BackButton>
          
          <PageHeader>
            <PageTitle>My Orders</PageTitle>
            <HeaderActions>
              <ActionButton onClick={() => setSortBy(sortBy === 'date' ? 'amount' : 'date')}>
                <FaSort /> Sort by {sortBy === 'date' ? 'Amount' : 'Date'}
              </ActionButton>
              <ActionButton onClick={() => setFilterStatus(filterStatus === 'all' ? 'delivered' : 'all')}>
                <FaFilter /> {filterStatus === 'all' ? 'Show Delivered' : 'Show All'}
              </ActionButton>
            </HeaderActions>
          </PageHeader>
          
          {loading ? (
            <LoadingContainer>
              <div className="spinner"></div>
              <p>Loading your amazing orders...</p>
            </LoadingContainer>
          ) : !user ? (
            <EmptyState>
              <FaBoxOpen className="icon" />
              <h3>Welcome! Please login to continue</h3>
              <p>Sign in to view your order history and track your purchases</p>
              <CTAButton onClick={() => navigate('/login')}>
                Login to Account
              </CTAButton>
            </EmptyState>
          ) : orders.length === 0 ? (
            <EmptyState>
              <FaShoppingBag className="icon" />
              <h3>No orders yet</h3>
              <p>Start your shopping journey and discover amazing products!</p>
              <CTAButton onClick={() => navigate('/products')}>
                <HiOutlineSparkles style={{ marginRight: '0.5rem' }} />
                Start Shopping
              </CTAButton>
            </EmptyState>
          ) : (
            <OrdersGrid>
              {orders.map((order, index) => {
                const statusColors = getStatusColor(order.status);
                return (
                  <OrderCard key={order.id} index={index}>
                    <OrderHeader>
                      <OrderTitle>
                        Order #{order.orderId || order.id.substring(0, 8)}
                      </OrderTitle>
                      <StatusBadge
                        bgColor={statusColors.bg}
                        textColor={statusColors.text}
                        iconColor={statusColors.icon}
                      >
                        {getStatusIcon(order.status?.toLowerCase())}
                        {order.status}
                      </StatusBadge>
                    </OrderHeader>
                    
                    <OrderMeta>
                      <FaCalendarAlt />
                      {new Date(order.date || order.createdAt).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </OrderMeta>
                    
                    <ItemsContainer>
                      {getFirstTwoItems(order.items).map((item, index) => (
                        <ItemRow key={index}>
                          <ItemImage 
                            src={item.image || '/placeholder-product.jpg'} 
                            alt={item.name}
                            onError={(e) => {
                              e.target.src = '/placeholder-product.jpg';
                            }}
                          />
                          <ItemDetails>
                            <ItemName>{item.name}</ItemName>
                            <ItemPrice>
                              {item.quantity} × ₹{(item.discountedPrice || item.price).toFixed(2)}
                              {item.discount && item.discount > 0 && (
                                <DiscountTag>
                                  {item.discount}% off
                                </DiscountTag>
                              )}
                            </ItemPrice>
                            <ItemMeta>
                              {item.selectedColor && (
                                <>
                                  <span>🎨 {item.selectedColor}</span>
                                  {item.selectedSize && <span>•</span>}
                                </>
                              )}
                              {item.selectedSize && (
                                <span>📏 {item.selectedSize}</span>
                              )}
                            </ItemMeta>
                          </ItemDetails>
                        </ItemRow>
                      ))}
                      {getTotalItemsCount(order.items) > 2 && (
                        <MoreItemsIndicator>
                          +{getTotalItemsCount(order.items) - 2} more amazing items
                        </MoreItemsIndicator>
                      )}
                    </ItemsContainer>
                    
                    <OrderFooter>
                      <ViewDetailsButton 
                        onClick={() => navigate(`/order-details/${order.id}`, { state: { order } })}
                      >
                        <FaEye /> View Details
                        <FaChevronRight />
                      </ViewDetailsButton>
                      <TotalPrice>
                        ₹{order.total?.toFixed(2) || '0.00'}
                      </TotalPrice>
                    </OrderFooter>
                  </OrderCard>
                );
              })}
            </OrdersGrid>
          )}
        </MainContent>
      </PageContainer>
      <Footer />
    </>
  );
};

export default OrdersPage;
